import type { PostHog } from "posthog-js";
import { acquisitionPageProperties } from "../public-discovery/acquisition-pages";
import type { PostHogEventName } from "./constants";

let _client: PostHog | null = null;
const clientListeners = new Set<() => void>();
let _eventContext: PostHogEventProperties = {};
let _identityState: PostHogIdentityState = { status: "disabled" };

type PendingPostHogEvent = {
  eventName: TrackablePostHogEventName;
  value?: string | number;
  properties?: PostHogEventProperties;
  requiresAuthenticatedIdentity?: boolean;
};

const MAX_PENDING_EVENTS = 100;
const _pendingEvents: PendingPostHogEvent[] = [];

export type PostHogEventProperty = string | number | boolean | null | undefined;
export type PostHogEventProperties = Record<string, PostHogEventProperty>;
export type TrackablePostHogEventName = PostHogEventName | "$pageview";
export type PostHogIdentityState =
  | { status: "disabled" }
  | { status: "pending"; target?: "pending" | "anonymous" | "authenticated" }
  | { status: "anonymous" }
  | { status: "authenticated"; userId: string };

type EventDisposition = "buffer" | "drop" | "log";

export function mergePostHogEventContexts(
  runtimeContext: PostHogEventProperties | undefined,
  identityContext: PostHogEventProperties | undefined,
): PostHogEventProperties {
  return {
    ...normalizeProperties(runtimeContext),
    ...normalizeProperties(identityContext),
  };
}

function normalizeProperties(
  properties?: PostHogEventProperties,
): PostHogEventProperties {
  if (!properties) return {};

  const normalized: PostHogEventProperties = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value !== undefined && value !== "") normalized[key] = value;
  }
  return normalized;
}

function withEventContext(
  properties?: PostHogEventProperties,
  value?: string | number,
): PostHogEventProperties | undefined {
  const eventProperties = {
    ..._eventContext,
    ...properties,
    ...(value === undefined ? {} : { event_value: value }),
  };
  return Object.keys(eventProperties).length > 0 ? eventProperties : undefined;
}

export function setPostHogClient(client: PostHog | null): void {
  const changed = _client !== client;
  _client = client;
  if (_client && Object.keys(_eventContext).length > 0)
    _client.register(_eventContext);
  flushPendingEvents();
  if (changed) for (const listener of clientListeners) listener();
}

export function getPostHogClient(): PostHog | null {
  return _client;
}

export function subscribeToPostHogClient(listener: () => void): () => void {
  clientListeners.add(listener);
  return () => {
    clientListeners.delete(listener);
  };
}

export function setPostHogIdentityState(state: PostHogIdentityState): void {
  _identityState = state;
  flushPendingEvents();
}

function getEventDisposition(
  properties?: PostHogEventProperties,
  requiresAuthenticatedIdentity = false,
): EventDisposition {
  if (_identityState.status === "disabled") return "drop";
  if (_identityState.status === "pending") return "buffer";

  const eventUserId = properties?.user_id;
  if (_identityState.status === "anonymous") {
    if (requiresAuthenticatedIdentity) return "buffer";
    return eventUserId ? "drop" : "log";
  }

  return eventUserId && eventUserId !== _identityState.userId ? "drop" : "log";
}

function flushPendingEvents(): void {
  if (!_client) return;
  if (_identityState.status === "pending") return;

  const pending = _pendingEvents.splice(0);
  const stillPending: PendingPostHogEvent[] = [];
  for (const event of pending) {
    const properties = withEventContext(event.properties, event.value);
    const disposition = getEventDisposition(
      properties,
      event.requiresAuthenticatedIdentity,
    );
    if (disposition === "log") {
      _client.capture(event.eventName, properties);
    } else if (disposition === "buffer") {
      stillPending.push(event);
    }
  }
  _pendingEvents.unshift(...stillPending);
}

export function setPostHogEventContext(context: PostHogEventProperties): void {
  _eventContext = normalizeProperties(context);
  _client?.register(_eventContext);
}

export function clearPostHogEventContext(): void {
  for (const key of Object.keys(_eventContext)) _client?.unregister(key);
  _eventContext = {};
}

function trackEventWithIdentityRequirement(
  eventName: TrackablePostHogEventName,
  value?: string | number,
  properties?: PostHogEventProperties,
  requiresAuthenticatedIdentity = false,
): boolean {
  const normalizedProperties = normalizeProperties(properties);
  const normalizedEventProperties = withEventContext(
    normalizedProperties,
    value,
  );
  const disposition = getEventDisposition(
    normalizedEventProperties,
    requiresAuthenticatedIdentity,
  );
  if (disposition === "drop") return false;
  if (!_client || disposition === "buffer") {
    if (_pendingEvents.length >= MAX_PENDING_EVENTS) _pendingEvents.shift();
    _pendingEvents.push({
      eventName,
      value,
      properties: normalizedProperties,
      requiresAuthenticatedIdentity,
    });
    return true;
  }

  _client.capture(eventName, normalizedEventProperties);
  return true;
}

export function trackEvent(
  eventName: TrackablePostHogEventName,
  value?: string | number,
  properties?: PostHogEventProperties,
): boolean {
  return trackEventWithIdentityRequirement(eventName, value, properties);
}

export function trackAuthenticatedEvent(
  eventName: TrackablePostHogEventName,
  value?: string | number,
  properties?: PostHogEventProperties,
): boolean {
  return trackEventWithIdentityRequirement(eventName, value, properties, true);
}

export function trackPageView(pathname: string, fromPath?: string): boolean {
  const currentUrl =
    typeof window === "undefined"
      ? pathname
      : new URL(pathname, window.location.origin).toString();

  return trackEvent("$pageview", undefined, {
    $current_url: currentUrl,
    $pathname: pathname,
    from_path: fromPath,
    ...acquisitionPageProperties(pathname),
  });
}
